/**
 * Ce que dit une graine : « À semer : mars à avril chez toi » ou, pour l'ail
 * et l'échalote qui se plantent, « À planter : octobre à novembre chez toi »
 * (K17). Un seul calcul, lu dans les périodes de semis et de plantation du
 * climat de l'adresse, que Mes graines, le calendrier et l'onboarding
 * partagent.
 */
export type SeedTimingKind = 'sow' | 'plant';

export interface SeedTiming {
  kind: SeedTimingKind;
  /** Mois de 1 à 12. */
  startMonth: number;
  endMonth: number;
}

export interface PeriodLike {
  type: string;
  startDate: Date | string;
  endDate: Date | string;
}

/** Mois en UTC : une fin de période au 30 avril à 23 h 59 UTC reste en avril à Bruxelles. */
const monthOf = (date: Date | string): number => new Date(date).getUTCMonth() + 1;

/**
 * Le semis prime : une graine se sème. Seule une culture qui n'a que des
 * périodes de plantation (bulbes, caïeux) dit « À planter ».
 */
export function seedTiming(periods: PeriodLike[] | null | undefined): SeedTiming | null {
  const list = periods ?? [];
  const sow = list.find((p) => p.type === 'SEEDLING');
  const chosen = sow ?? list.find((p) => p.type === 'PLANTING');
  if (!chosen) return null;
  return { kind: sow ? 'sow' : 'plant', startMonth: monthOf(chosen.startDate), endMonth: monthOf(chosen.endDate) };
}

/**
 * La fenêtre couvre-t-elle ce mois (1 à 12) ? Elle peut passer l'hiver :
 * « octobre à mars » couvre décembre.
 */
export function isTimingInMonth(timing: SeedTiming, month: number): boolean {
  const { startMonth, endMonth } = timing;
  return startMonth <= endMonth
    ? month >= startMonth && month <= endMonth
    : month >= startMonth || month <= endMonth;
}

/** Nom du mois dans la langue de l'interface (« mars », « März », « March »). */
export function monthName(month: number, lang: string): string {
  return new Intl.DateTimeFormat((lang || 'fr').toLowerCase(), { month: 'long', timeZone: 'UTC' })
    .format(new Date(Date.UTC(2026, month - 1, 15)));
}

/** Jour et mois d'une date sans année (`MM-DD`), dans la langue de l'interface (« 20 avril »). */
export function dayMonthName(mmdd: string, lang: string): string {
  const [month, day] = mmdd.split('-').map(Number);
  return new Intl.DateTimeFormat((lang || 'fr').toLowerCase(), { day: 'numeric', month: 'long', timeZone: 'UTC' })
    .format(new Date(Date.UTC(2026, month - 1, day)));
}
